import { TWidget } from 'widgets/Widget';

declare const window: ExtendedWindow & {
    friendlyChallenge: {
        WidgetInstance: (fcContainer: object) => void;
    };
};

type TFriendlyCaptchaWidget = {
    hasBeenStarted: boolean;

    reset: () => void;
    start: () => void;
    init: () => void;
};

/**
 * @description FriendlyCaptcha implementation
 * @param Widget Base widget for extending
 * @returns FriendlyCaptcha class
 */
function FriendlyCaptchaClassCreator(Widget: TWidget) {
    class FriendlyCaptcha extends Widget {
        /**
         * @description Widget initialization logic
         * @listens 'captcha.reset.on.error'
         * @listens 'captcha.init'
         */
        init() {
            super.init();
            this.eventBus().on('captcha.reset.on.error', 'resetCaptchaOnError');
            this.eventBus().on('captcha.init', 'initCaptchaWidget');
        }

        /**
         * @description Resets the friendly captcha widget on error and disables submit buttons
         * @param formSelector - The selector for the form
         */
        resetCaptchaOnError(formSelector) {
            this.resetCaptchaWidget(formSelector + ' .frc-captcha');
        }

        /**
         * @description Returns the friendly captcha widget instance
         * @param selector - The selector for the captcha widget
         */
        getCaptchaWidget(selector) {
            let widget = null;

            const form = this.ref('self').get()?.closest('form');
            const fcContainer = form?.querySelector(selector);

            if (fcContainer && typeof window.friendlyChallenge !== 'undefined') {
                widget = new window.friendlyChallenge.WidgetInstance(fcContainer);
            }

            return widget;
        }

        /**
         * @description Resets the friendly captcha widget
         * @param selector - The selector for the captcha widget
         * @param restart - Whether to restart the widget after resetting
         */
        resetCaptchaWidget(selector, restart = false) {
            const widget = this.getCaptchaWidget(selector) as unknown as TFriendlyCaptchaWidget;

            if (widget) {
                widget.reset();

                if (restart && !widget.hasBeenStarted) {
                    widget.start();
                }
            }
        }

        /**
         * @description Initializes the friendly captcha widget
         * @param formSelector - The selector for the form
         */
        initCaptchaWidget(formSelector) {
            const widget = this.getCaptchaWidget(formSelector + ' .frc-captcha') as unknown as TFriendlyCaptchaWidget;

            if (widget) {
                widget.init();
            }
        }
    }

    return FriendlyCaptcha;
}

export type TFriendlyCaptcha = ReturnType<typeof FriendlyCaptchaClassCreator>;

export type TFriendlyCaptchaInstance = InstanceType<TFriendlyCaptcha>;

export default FriendlyCaptchaClassCreator;
